import { MapPin, Target, CheckCircle2, TrendingUp, Search, FileCheck, Euro, ExternalLink } from 'lucide-react';
import { FadeInSection } from '@/components/ui/FadeInSection';
import { Button } from '@/components/ui/button';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

interface UseCase {
  client: string;
  context: string;
  challenge: string;
  solution: string;
  result: string;
  image: string;
  tags?: string[];
  articleSlug?: string;
  articleImage?: string;
  articleTitle?: string;
  externalLink?: string;
}

interface UseCaseCardProps {
  useCase: UseCase;
  index: number;
  accentColor?: 'green' | 'blue' | 'red' | 'purple' | 'orange';
}

/**
 * Card cas d'usage format Binaree - Layout 2 colonnes avec highlights et checklist
 */
export function UseCaseCard({ useCase, index, accentColor = 'green' }: UseCaseCardProps) {
  const { t, i18n } = useTranslation('prestations');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';
  // Configuration des couleurs selon l'accent
  const colorConfig = {
    green: {
      bg1: 'bg-aspexit-green/10',
      bg2: 'bg-aspexit-orange/10',
      gradient: 'from-aspexit-green/10 to-aspexit-orange/10',
      icon: 'text-aspexit-green',
      badge: 'bg-aspexit-red/10 text-aspexit-red',
      tag: 'bg-aspexit-green/10 text-aspexit-green',
      highlight1: 'bg-aspexit-green/10 text-aspexit-green',
      highlight2: 'bg-aspexit-orange/10 text-aspexit-orange',
      highlight3: 'bg-aspexit-purple/10 text-aspexit-purple',
      check: 'text-aspexit-green',
      button: 'bg-aspexit-green hover:bg-aspexit-green-dark',
    },
    blue: {
      bg1: 'bg-blue-100/50',
      bg2: 'bg-blue-200/50',
      gradient: 'from-blue-100/50 to-blue-200/50',
      icon: 'text-blue-600',
      badge: 'bg-blue-100 text-blue-700',
      tag: 'bg-blue-100 text-blue-700',
      highlight1: 'bg-blue-100 text-blue-700',
      highlight2: 'bg-blue-200 text-blue-800',
      highlight3: 'bg-blue-300 text-blue-900',
      check: 'text-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700',
    },
    red: {
      bg1: 'bg-aspexit-red/10',
      bg2: 'bg-red-200/10',
      gradient: 'from-aspexit-red/10 to-red-200/10',
      icon: 'text-aspexit-red',
      badge: 'bg-aspexit-red/10 text-aspexit-red',
      tag: 'bg-aspexit-red/10 text-aspexit-red',
      highlight1: 'bg-aspexit-red/10 text-aspexit-red',
      highlight2: 'bg-red-100 text-red-700',
      highlight3: 'bg-red-200 text-red-800',
      check: 'text-aspexit-red',
      button: 'bg-aspexit-red hover:bg-red-700',
    },
    purple: {
      bg1: 'bg-aspexit-purple/10',
      bg2: 'bg-purple-200/10',
      gradient: 'from-aspexit-purple/10 to-purple-200/10',
      icon: 'text-aspexit-purple',
      badge: 'bg-aspexit-purple/10 text-aspexit-purple',
      tag: 'bg-aspexit-purple/10 text-aspexit-purple',
      highlight1: 'bg-aspexit-purple/10 text-aspexit-purple',
      highlight2: 'bg-purple-100 text-purple-700',
      highlight3: 'bg-purple-200 text-purple-800',
      check: 'text-aspexit-purple',
      button: 'bg-aspexit-purple hover:bg-purple-700',
    },
    orange: {
      bg1: 'bg-aspexit-orange/10',
      bg2: 'bg-orange-200/10',
      gradient: 'from-aspexit-orange/10 to-orange-200/10',
      icon: 'text-aspexit-orange',
      badge: 'bg-aspexit-orange/10 text-aspexit-orange',
      tag: 'bg-aspexit-orange/10 text-aspexit-orange',
      highlight1: 'bg-aspexit-orange/10 text-aspexit-orange',
      highlight2: 'bg-orange-100 text-orange-700',
      highlight3: 'bg-orange-200 text-orange-800',
      check: 'text-aspexit-orange',
      button: 'bg-aspexit-orange hover:bg-orange-700',
    },
  };

  const colors = colorConfig[accentColor];

  // Extraire les points clés du résultat pour la checklist
  const checklistItems = [
    useCase.challenge,
    useCase.solution,
    useCase.result
  ];

  return (
    <FadeInSection delay={index * 0.1}>
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 p-8 lg:p-12">
          {/* Colonne gauche - Illustration */}
          <div className="flex items-center justify-center">
            <div className="relative w-full max-w-md">
              {/* Cercles décoratives en arrière-plan */}
              <div className={`absolute top-0 left-0 w-32 h-32 ${colors.bg1} rounded-full blur-3xl`} />
              <div className={`absolute bottom-0 right-0 w-40 h-40 ${colors.bg2} rounded-full blur-3xl`} />

              {/* Illustration principale */}
              <div className={`relative rounded-xl overflow-hidden ${useCase.articleImage ? '' : `bg-gradient-to-br ${colors.gradient}`} min-h-[320px] flex items-center justify-center shadow-lg`}>
                {useCase.articleImage ? (
                  <OptimizedImage
                    src={useCase.articleImage}
                    alt={useCase.articleTitle || useCase.client}
                    className="w-full h-full object-cover"
                    aspectRatio="16/9"
                  />
                ) : (
                  <div className="relative z-10 text-center p-8">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white shadow-xl flex items-center justify-center">
                      <TrendingUp className={`w-12 h-12 ${colors.icon}`} />
                    </div>
                    <p className="text-lg font-semibold text-aspexit-black">
                      {t('useCase.caseStudy', { number: index + 1 })}
                    </p>
                    <p className="text-sm text-aspexit-gray mt-2">
                      {useCase.client}
                    </p>
                  </div>
                )}
              </div>

              {/* Tags en dessous */}
              {useCase.tags && (
                <div className="flex flex-wrap gap-2 mt-4 justify-center">
                  {useCase.tags.map((tag, i) => (
                    <span
                      key={i}
                      className={`px-3 py-1 ${colors.tag} text-xs font-medium rounded-full`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Colonne droite - Contenu */}
          <div className="flex flex-col">
            {/* Badge */}
            <div className="mb-4">
              <span className={`inline-block px-3 py-1 ${colors.badge} text-xs font-bold uppercase tracking-wider rounded-md`}>
                {t('useCase.badge')}
              </span>
            </div>

            {/* Titre */}
            <h3 className="text-3xl lg:text-4xl font-heading font-black text-aspexit-black mb-4 leading-tight">
              {useCase.client}
            </h3>

            {/* Sous-titre / Contexte */}
            <p className="text-base text-aspexit-gray mb-8 leading-relaxed">
              {useCase.context}
            </p>

            {/* 2 Highlights en colonnes */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="text-center">
                <div className={`w-12 h-12 mx-auto mb-2 rounded-lg ${colors.highlight1} flex items-center justify-center`}>
                  <Search className={`w-6 h-6 ${colors.icon}`} />
                </div>
                <p className="text-xs font-semibold text-aspexit-black mb-1">{t('useCase.highlights.analysis')}</p>
                <p className="text-xs text-aspexit-gray">{t('useCase.highlights.analysisDesc')}</p>
              </div>
              <div className="text-center">
                <div className={`w-12 h-12 mx-auto mb-2 rounded-lg ${colors.highlight2} flex items-center justify-center`}>
                  <FileCheck className={`w-6 h-6 ${colors.icon}`} />
                </div>
                <p className="text-xs font-semibold text-aspexit-black mb-1">{t('useCase.highlights.expertise')}</p>
                <p className="text-xs text-aspexit-gray">{t('useCase.highlights.expertiseDesc')}</p>
              </div>
            </div>

            {/* Checklist */}
            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle2 className={`w-5 h-5 ${colors.check} flex-shrink-0 mt-0.5`} />
                <span className="text-sm text-aspexit-gray leading-relaxed">
                  <strong className="text-aspexit-black">{t('useCase.labels.challenge')}</strong> {useCase.challenge}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className={`w-5 h-5 ${colors.check} flex-shrink-0 mt-0.5`} />
                <span className="text-sm text-aspexit-gray leading-relaxed">
                  <strong className="text-aspexit-black">{t('useCase.labels.solution')}</strong> {useCase.solution}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className={`w-5 h-5 ${colors.check} flex-shrink-0 mt-0.5`} />
                <span className="text-sm text-aspexit-gray leading-relaxed">
                  <strong className="text-aspexit-black">{t('useCase.labels.result')}</strong> {useCase.result}
                </span>
              </li>
            </ul>

            {/* Boutons CTA */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                className={`${colors.button} text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-lg`}
                asChild
              >
                <a href={`/${currentLocale}/contact`}>
                  {t('useCase.cta')}
                </a>
              </Button>
              {(useCase.articleSlug || useCase.externalLink) && (
                <Button
                  variant="outline"
                  className="font-semibold px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-lg border-2"
                  asChild
                >
                  <a
                    href={useCase.externalLink || `/${currentLocale}/blog/${useCase.articleSlug}`}
                    className="flex items-center gap-2"
                    {...(useCase.externalLink && { target: '_blank', rel: 'noopener noreferrer' })}
                  >
                    {t('useCase.readArticle') || 'Lire l\'article'}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </FadeInSection>
  );
}
